import { CheckCircleFilled } from "@ant-design/icons";
import { Divider, notification, Skeleton } from "antd";
import "dayjs/locale/fr";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { FaRegTimesCircle, FaTimesCircle } from "react-icons/fa";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { Link, useNavigate, useParams } from "react-router-dom";
import Logo from "../../../assets/media/logo/Logo_horizontal.svg";
import { Button } from "../../../stories/Button/Button";
import { CustomModal } from "../../../stories/Modal/CustomModal";
import { PartnersLink } from "../../../stories/PartnersLink/PartnersLink";
import { UpdatePatientData } from "../../../utils/requests/patientReport";
import { GetUserSettings } from "../../../utils/requests/patientSetting";
import { Brand } from "../../ThemeConfig";
import { ChangeFrontLang } from "./ChangeFrontLang";

/**
 * Enumeration for reasons why patient won't take action
 */
type WontTakeActionReason =
  | "NOT_ENOUGH_TIME"
  | "NOT_MOTIVATED"
  | "SKEPTICAL_ABOUT_SCREENINGS"
  | "WORRIED_ABOUT_RESULTS"
  | "OTHER";

/**
 * Interface for onboarding by email data
 * @interface OnboardingByEmailData
 */
interface OnboardingByEmailData {
  uid: string;
  learnedInformationAboutHealth: boolean | null;
  planToTakeAppointement: boolean | null;
  wontTakeActionsBecause: WontTakeActionReason | null;
  refuseAppointment: boolean | null
}

/**
 * RdvWithDoctor component
 * @returns
 */
export const OnboardingByEmail: React.FC<{}> = () => {
  const [loading, setLoading] = React.useState(true);
  const params = useParams();
  const [userData, setData] = useState<OnboardingByEmailData | null>(null);
  const [showNextStep, setOpen] = useState<boolean>(false);
  const [isOk, setOk] = useState<boolean>(false);
  const [openStep3, setOpenStep3] = useState<boolean>(false);
  const [openStep4, setOpenStep4] = useState<boolean>(false);
  const [api, contextHolder] = notification.useNotification();
  const nav = useNavigate();
  const { t } = useTranslation(["common", "patient"]);

  /**
   * return notification
   * @param title - notification title
   * @param description - notification description
   * @param icon - notification icon
   * @param duration - notification duration in seconds
   */
  const openNotification = (
    title: React.ReactNode | string,
    description: React.ReactNode | string,
    icon: React.ReactNode | null,
    duration: number | null
  ) => {
    api.open({
      message: title,
      description: description,
      icon: icon,
      placement: "bottomRight",
      duration: duration || 4.5,
    });
  };

  const user_uid = params.uid;

  React.useEffect(() => {
    GetUserSettings(user_uid || "")
      ?.then((response) => {
        if (response.data?.patient?.uid) {
          setData({
            uid: response.data?.patient?.uid,
            learnedInformationAboutHealth: null,
            planToTakeAppointement: null,
            wontTakeActionsBecause: null,
            refuseAppointment: null
          });
        }
      })
      ?.catch(console.error)
      ?.finally(() => {
        setLoading(false);
      });
    //eslint-disable-next-line
  }, []);

  /**
   * Handle form submission
   * @param userData - onboarding data to submit
   */
  const handleSubmit = (userData: OnboardingByEmailData) => {
    UpdatePatientData({
      data: userData,
    })
      .then(() => {
        // Refresh user data
        setOk(true);
        if (userData?.planToTakeAppointement === true) {
          setOpen(true);
        }
      })
      .catch((error) => {
        openNotification(
          <span className="flex items-center font-semibold">
            {" "}
            {t("common:error")} {error?.status}
          </span>,
          error?.message,
          <FaRegTimesCircle className="text-danger mr-2" />,
          10
        );
        setOk(false);
      });
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {Brand.name} - {t("patient:rdv_with_doctor_page.page_title")}
        </title>
        <link rel="canonical" href={process.env.HOST + "/login"} />
      </Helmet>
      {/* New content */}
      <div className="flex flex-col xl:flex-row bg-white">
        {/* Main content */}
        <div className="flex flex-col w-full min-h-[550px] h-screen p-6 justify-between items-center">
          {/* Login Header */}
          <div className="flex flex-col sm:flex-row w-full justify-between md:items-center gap-2">
            <div className="relative flex gap-4 logo-box justify-start items-center">
              <Link to="/" className="text-secondary underline">
                <img className="relative mx-auto h-[28px]" src={Logo} alt="Lianeli" />
              </Link>
            </div>
            
            <div className="flex flex-row flex-wrap gap-2 items-center mt-4 mb-4 sm:mt-0 sm:mb-0">
              <button
                onClick={() => nav('/login')}
                className="bg-white text-sm text--default border border-gray-300 px-3 py-1 rounded-md shadow-sm 
                          hover:bg-gray-50 focus:bg-gray-100 active:bg-gray-100
                          transition whitespace-nowrap h-[32px] flex"
              >
                {t('common:login_page.login')}
              </button>
              <ChangeFrontLang />
            </div>
          </div>
          {/* Login content */}
          <div className="flex flex-col gap-4 mx-auto max-w-4xl w-full">
            {loading ? (
              <>
                <div className="form-container my-0">
                  <Skeleton active />
                </div>
              </>
            ) : (
              <>
                {userData !== null && showNextStep === false && (
                  <>
                    {userData?.uid !== null &&
                      userData?.uid !== undefined &&
                      isOk === false && (
                        <section className="flex flex-col gap-4">
                          {contextHolder}

                          <div className="flex flex-col justify-center items-center md:items-start px-0 py-10 min-h-[550px]">
                            <h4>
                              {t("common:onboarding_by_email.main_title")}
                            </h4>
                            <div className="mt-4 border-t border-gray-100">
                              <dl className="divide-y divide-gray-100">
                                <div className="flex flex-col items-start justify-between py-4 gap-3">
                                  <dt className="flex flex-col text-[14px] leading-6 ">
                                    {t(
                                      "common:onboarding_by_email.questions.question_1"
                                    )}
                                  </dt>
                                  <dd className="mt-1 text-[15px] leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                    <div className="flex gap-2">
                                      <button
                                        className={`btn rounded-md ${
                                          userData?.learnedInformationAboutHealth ===
                                          true
                                            ? "btn--secondary"
                                            : "btn--default"
                                        }`}
                                        onClick={() => {
                                          setData({
                                            ...userData,
                                            learnedInformationAboutHealth: true,
                                            planToTakeAppointement: null,
                                            refuseAppointment: null,
                                            wontTakeActionsBecause: null,
                                          });
                                        }}
                                      >
                                        {t("common:yes")}
                                      </button>
                                      <button
                                        className={`btn rounded-md ${
                                          userData?.learnedInformationAboutHealth ===
                                          false
                                            ? "btn--secondary"
                                            : "btn--default"
                                        }`}
                                        onClick={() => {
                                          setData({
                                            ...userData,
                                            learnedInformationAboutHealth:
                                              false,
                                            planToTakeAppointement: null,
                                            refuseAppointment: null,
                                            wontTakeActionsBecause: null,
                                          });
                                        }}
                                      >
                                        {t("common:no")}
                                      </button>
                                    </div>
                                  </dd>
                                </div>

                                <div className="flex flex-col items-start justify-between py-4 gap-3">
                                  <dt className="flex flex-col text-[14px] leading-6 ">
                                    {t(
                                      "common:onboarding_by_email.questions.question_2"
                                    )}
                                  </dt>
                                  <dd className="mt-1 text-[15px] leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                    <div className="flex gap-2">
                                      <button
                                        className={`btn rounded-md ${
                                          userData?.planToTakeAppointement ===
                                          true
                                            ? "btn--secondary"
                                            : "btn--default"
                                        }`}
                                        onClick={() => {
                                          setData({
                                            ...userData,
                                            planToTakeAppointement: true,
                                            refuseAppointment: null,
                                            wontTakeActionsBecause: null,
                                          });
                                        }}
                                      >
                                        {t("common:yes")}
                                      </button>
                                      <button
                                        className={`btn rounded-md ${
                                          userData?.planToTakeAppointement ===
                                          false
                                            ? "btn--secondary"
                                            : "btn--default"
                                        }`}
                                        onClick={() => {
                                          setData({
                                            ...userData,
                                            planToTakeAppointement: false,
                                            refuseAppointment: true,
                                            wontTakeActionsBecause: null,
                                          });
                                        }}
                                      >
                                        {t("common:no")}
                                      </button>
                                    </div>
                                  </dd>
                                </div>

                                {userData?.planToTakeAppointement === false && (
                                  <div className="flex flex-col items-start justify-between py-4 gap-3">
                                    <dt className="flex flex-col text-[14px] leading-6 ">
                                      {t(
                                        "common:onboarding_by_email.questions.question_3.title"
                                      )}
                                    </dt>
                                    <dd className="mt-1 text-[15px] leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                      <div className="flex flex-wrap gap-2">
                                        <button
                                          className={`btn rounded-md ${
                                            userData?.wontTakeActionsBecause ===
                                            "NOT_ENOUGH_TIME"
                                              ? "btn--secondary"
                                              : "btn--default"
                                          }`}
                                          onClick={() => {
                                            setData({
                                              ...userData,
                                              wontTakeActionsBecause:
                                                "NOT_ENOUGH_TIME",
                                            });
                                          }}
                                        >
                                          {t(
                                            "common:onboarding_by_email.questions.question_3.responses.NOT_ENOUGH_TIME"
                                          )}
                                        </button>
                                        <button
                                          className={`btn rounded-md ${
                                            userData?.wontTakeActionsBecause ===
                                            "NOT_MOTIVATED"
                                              ? "btn--secondary"
                                              : "btn--default"
                                          }`}
                                          onClick={() => {
                                            setData({
                                              ...userData,
                                              wontTakeActionsBecause:
                                                "NOT_MOTIVATED",
                                            });
                                          }}
                                        >
                                          {t(
                                            "common:onboarding_by_email.questions.question_3.responses.NOT_MOTIVATED"
                                          )}
                                        </button>
                                        <button
                                          className={`btn rounded-md ${
                                            userData?.wontTakeActionsBecause ===
                                            "SKEPTICAL_ABOUT_SCREENINGS"
                                              ? "btn--secondary"
                                              : "btn--default"
                                          }`}
                                          onClick={() => {
                                            setData({
                                              ...userData,
                                              wontTakeActionsBecause:
                                                "SKEPTICAL_ABOUT_SCREENINGS",
                                            });
                                          }}
                                        >
                                          {t(
                                            "common:onboarding_by_email.questions.question_3.responses.SKEPTICAL_ABOUT_SCREENINGS"
                                          )}
                                        </button>
                                        <button
                                          className={`btn rounded-md ${
                                            userData?.wontTakeActionsBecause ===
                                            "WORRIED_ABOUT_RESULTS"
                                              ? "btn--secondary"
                                              : "btn--default"
                                          }`}
                                          onClick={() => {
                                            setData({
                                              ...userData,
                                              wontTakeActionsBecause:
                                                "WORRIED_ABOUT_RESULTS",
                                            });
                                          }}
                                        >
                                          {t(
                                            "common:onboarding_by_email.questions.question_3.responses.WORRIED_ABOUT_RESULTS"
                                          )}
                                        </button>
                                        <button
                                          className={`btn rounded-md ${
                                            userData?.wontTakeActionsBecause ===
                                            "OTHER"
                                              ? "btn--secondary"
                                              : "btn--default"
                                          }`}
                                          onClick={() => {
                                            setData({
                                              ...userData,
                                              wontTakeActionsBecause: "OTHER",
                                            });
                                          }}
                                        >
                                          {t(
                                            "common:onboarding_by_email.questions.question_3.responses.OTHER"
                                          )}
                                        </button>
                                      </div>
                                    </dd>
                                  </div>
                                )}

                                <div className="flex w-full items-center justify-start px-0 py-4 my-0 gap-2 bg-white border-t-[1px] border-slate-100">
                                  <button
                                    className={`btn ${
                                      userData?.learnedInformationAboutHealth ===
                                        null ||
                                      userData?.planToTakeAppointement ===
                                        null ||
                                      (userData?.planToTakeAppointement ===
                                        false &&
                                        userData?.wontTakeActionsBecause ===
                                          null)
                                        ? "btn--disabled"
                                        : "btn--primary"
                                    } w-full h-12 text-[14px] md:text-md min-w-[300px] justify-center rounded-md`}
                                    disabled={
                                      userData?.learnedInformationAboutHealth ===
                                        null ||
                                      userData?.planToTakeAppointement ===
                                        null ||
                                      (userData?.planToTakeAppointement ===
                                        false &&
                                        userData?.wontTakeActionsBecause ===
                                          null)
                                    }
                                    onClick={() => {
                                      if (userData) {
                                        handleSubmit(userData);
                                      }
                                    }}
                                  >
                                    {t(
                                      "common:onboarding_by_email.buttons.save_and_continue"
                                    )}
                                  </button>
                                </div>
                              </dl>
                            </div>
                          </div>
                        </section>
                      )}

                    {userData?.planToTakeAppointement === false &&
                      isOk === true && (
                        <>
                          <div className="flex flex-col gap-2 justify-center items-center">
                            <div className="flex flex-col items-center justify-center gap-4 my-4">
                              <CheckCircleFilled className="text-7xl text--success" />
                              <span>
                                {t(
                                  "common:onboarding_by_email.submit_responses"
                                )}
                              </span>
                            </div>
                            <div className="flex max-w-[500px]">
                              <Button
                                type="button"
                                format="default"
                                color="primary"
                                className="h-10 w-full text-[13px] justify-center"
                                onClick={() => (window.location.href = "/")}
                              >
                                {t(
                                  "patient:rdv_with_doctor_page.continue_to_account"
                                )}
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                  </>
                )}

                {showNextStep === true && isOk === true && (
                  <div className="flex flex-col gap-6">
                    {/* Success message */}
                    <div className="main--box-success relative flex w-full justify-start items-center gap-3 bg-white shadow-sm px-4 py-3 my-6 rounded-xl overflow-hidden border-l-4 border-green-400">
                      <span className="icon--box-success hidden sm:flex items-center justify-center min-w-[40px] shadow-md rounded-md border--box-success py-2">
                        <IoMdInformationCircleOutline className="w-5 h-5 text--success" />
                      </span>
                      <p className="text-[14px] md:text-md text-gray-700">
                        {t(
                          "common:onboarding_by_email.next_steps.success_title"
                        )}
                      </p>
                    </div>

                    {/* Steps completed */}
                    <div className="flex flex-col">
                      <div className="flex flex-row gap-4 items-center p-3">
                        <span>
                          <CheckCircleFilled className="text-2xl text--success" />
                        </span>
                        <span className="text-[14px] md:text-md text-gray-300 line-through">
                          {t("common:onboarding_by_email.next_steps.step_1")}
                        </span>
                      </div>
                      <Divider className="my-2" />
                      <div className="flex flex-row gap-4 items-center p-3">
                        <span>
                          <CheckCircleFilled className="text-2xl text--success" />
                        </span>
                        <span className="text-[14px] md:text-md text-gray-300 line-through">
                          {t("common:onboarding_by_email.next_steps.step_2")}
                        </span>
                      </div>
                      <Divider className="my-2" />
                      <div className="flex flex-row gap-4 items-center group cursor-pointer hover:bg-blue-50 p-3 rounded-lg transition-colors">
                        <span className="text--secondary">→</span>
                        <button
                          className="text-[14px] md:text-md text--secondary hover:text--secondary hover:underline font-medium"
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenStep3(true);
                          }}
                        >
                          {t(
                            "common:onboarding_by_email.next_steps.step_3.title"
                          )}
                        </button>
                      </div>
                      <Divider className="my-2" />
                      <div className="flex flex-row gap-4 items-center group cursor-pointer hover:bg-blue-50 p-3 rounded-lg transition-colors">
                        <span className="text--secondary">→</span>
                        <button
                          className="text-[14px] md:text-md text--secondary hover:text--secondary hover:underline font-medium"
                          onClick={(e) => {
                            e.preventDefault();
                            // Navigation vers l'étape 4
                            setOpenStep4(true);
                          }}
                        >
                          {t(
                            "common:onboarding_by_email.next_steps.step_4.title"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* 404 */}
                {userData === null && isOk === false && (
                  <>
                    <div className="flex flex-col gap-2 justify-center items-center">
                      <div className="flex flex-col items-center justify-center gap-4 my-4">
                        <FaTimesCircle className="text-7xl text--danger" />
                        <span>
                          {t("patient:rdv_with_doctor_page.not_found")}
                        </span>
                      </div>

                      <div className="flex justify-center items-center max-w-[500px]">
                        <Button
                          type="button"
                          format="default"
                          color="primary"
                          className="h-10 w-full text-[13px] justify-center"
                          onClick={() => (window.location.href = "/")}
                        >
                          {t(
                            "patient:rdv_with_doctor_page.continue_to_account"
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          {/* Login footer */}
          <div className="flex w-full justify-center items-center">
            <span className="text--grey">{t("common:legal_rights")}</span>
          </div>
        </div>

        {/* OPEN BOX FOR STEP 3 */}
        {openStep3 && (
          <CustomModal
            onClose={() => {
              setOpenStep3(false);
            }}
            title={
              <>
                <div className="flex items-center justify-start flex-row gap-2 my-3">
                  <span>
                    <CheckCircleFilled className="text-[15px] text--success" />
                  </span>
                  <span className="text-[14px] md:text-md font-normal">
                    {t(
                      "common:onboarding_by_email.next_steps.step_3.popin.step_level"
                    )}
                  </span>
                </div>
                <span className="text-[16px]">
                  {t(
                    "common:onboarding_by_email.next_steps.step_3.popin.title"
                  )}
                </span>
              </>
            }
          >
            <div className="flex flex-col w-full">
              <p className="text-[13px] md:text-md">
                {t("common:onboarding_by_email.next_steps.step_3.popin.p_1")}
              </p>
              <ul className="list-disc list-inside pl-6 text-[13px] md:text-md py-3">
                <li>
                  {t(
                    "common:onboarding_by_email.next_steps.step_3.popin.ul.li_1"
                  )}
                </li>
                <li>
                  {t(
                    "common:onboarding_by_email.next_steps.step_3.popin.ul.li_2"
                  )}
                </li>
                <li>
                  {t(
                    "common:onboarding_by_email.next_steps.step_3.popin.ul.li_3"
                  )}
                </li>
              </ul>
              <p className="text-[13px] md:text-md">
                {t("common:onboarding_by_email.next_steps.step_3.popin.p_2")}
              </p>
              <PartnersLink handleClick={() => {}} />
            </div>
          </CustomModal>
        )}

        {/* OPEN BOX FOR STEP 4 */}
        {openStep4 && (
          <CustomModal
            onClose={() => {
              setOpenStep4(false);
            }}
            title={
              <>
                <div className="flex items-center justify-start flex-row gap-2 my-3">
                  <span>
                    <CheckCircleFilled className="text-[15px] text--success" />
                  </span>
                  <span className="text-[14px] md:text-md font-normal">
                    {t(
                      "common:onboarding_by_email.next_steps.step_4.popin.step_level"
                    )}
                  </span>
                </div>
                <span className="text-[16px]">
                  {t(
                    "common:onboarding_by_email.next_steps.step_4.popin.title"
                  )}
                </span>
              </>
            }
          >
            <div className="flex flex-col w-full">
              <p className="text-[13px] md:text-md my-3">
                {t("common:onboarding_by_email.next_steps.step_4.popin.p_1")}
              </p>
              <p className="text-[13px] md:text-md">
                {t("common:onboarding_by_email.next_steps.step_4.popin.p_2")}
              </p>

              <Button
                type="button"
                format="default"
                color="primary"
                className="h-10 w-full text-[13px] justify-center mt-6"
                onClick={() => (window.location.href = "/")}
              >
                {t("common:login_page.login")}
              </Button>
            </div>
          </CustomModal>
        )}
      </div>
    </>
  );
};

export default OnboardingByEmail;
